import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  useUsers,
  useUpdateUser,
  useAssignOperator,
  useUnassignOperator,
  useAssignAdmin,
  useUnassignAdmin,
} from "@/api/use-user-api";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, UserPlus, Truck, Pencil, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const roleColors: Record<string, string> = {
  ADMIN: "bg-purple-500",
  OPERATOR: "bg-blue-500",
  CUSTOMER: "bg-orange-500",
};

export function UsersTable() {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignOperatorDialogOpen, setAssignOperatorDialogOpen] =
    useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    name: string;
    operator?: {
      id: string;
      dispatchCenterId?: string | null;
      depotId?: string | null;
    } | null;
  } | null>(null);
  const [editUser, setEditUser] = useState<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
  } | null>(null);
  const [unassignType, setUnassignType] = useState<"operator" | "admin" | null>(
    null
  );
  const [selectedDispatchCenterId, setSelectedDispatchCenterId] =
    useState<string>("");
  const [assignAdminDialogOpen, setAssignAdminDialogOpen] = useState(false);

  const { data: users, isLoading } = useUsers();
  const { data: dispatchCenters } = useGetDispatchCenters();

  const updateUser = useUpdateUser();
  const assignOperator = useAssignOperator();
  const unassignOperator = useUnassignOperator();
  const assignAdmin = useAssignAdmin();
  const unassignAdmin = useUnassignAdmin();

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || "U";
  };

  const getDisplayName = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    return "Unknown";
  };

  const handleEditUser = (user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  }) => {
    setEditUser({
      id: user.id,
      email: user.email,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      phone: user.phone || "",
    });
    setEditDialogOpen(true);
  };

  const confirmEditUser = () => {
    if (!editUser) return;

    updateUser.mutate(
      {
        userId: editUser.id,
        firstName: editUser.firstName || undefined,
        lastName: editUser.lastName || undefined,
        phone: editUser.phone || undefined,
      },
      {
        onSuccess: () => {
          toast.success("User updated");
          setEditDialogOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to update user");
        },
      }
    );
  };

  const handleAssignOperator = (user: typeof selectedUser) => {
    setSelectedUser(user);
    setSelectedDispatchCenterId("");
    setAssignOperatorDialogOpen(true);
  };

  const handleUnassignOperator = (user: typeof selectedUser) => {
    setSelectedUser(user);
    setUnassignType("operator");
    setUnassignDialogOpen(true);
  };

  const handleAssignAdmin = (user: typeof selectedUser) => {
    setSelectedUser(user);
    setAssignAdminDialogOpen(true);
  };

  const handleUnassignAdmin = (user: typeof selectedUser) => {
    setSelectedUser(user);
    setUnassignType("admin");
    setUnassignDialogOpen(true);
  };

  const confirmAssignOperator = () => {
    if (!selectedUser || !selectedDispatchCenterId) return;

    assignOperator.mutate(
      { userId: selectedUser.id, dispatchCenterId: selectedDispatchCenterId },
      {
        onSuccess: () => {
          toast.success("User assigned as operator");
          setAssignOperatorDialogOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to assign operator");
        },
      }
    );
  };

  const confirmAssignAdmin = () => {
    if (!selectedUser) return;

    assignAdmin.mutate(selectedUser.id, {
      onSuccess: () => {
        toast.success("Admin role assigned");
        setAssignAdminDialogOpen(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to assign admin role");
      },
    });
  };

  const confirmUnassign = () => {
    if (!selectedUser) return;

    if (unassignType === "operator") {
      unassignOperator.mutate(selectedUser.id, {
        onSuccess: () => {
          toast.success("Operator role removed");
          setUnassignDialogOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to remove operator role");
        },
      });
    } else if (unassignType === "admin") {
      unassignAdmin.mutate(selectedUser.id, {
        onSuccess: () => {
          toast.success("Admin role removed");
          setUnassignDialogOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to remove admin role");
        },
      });
    }
  };

  const getDispatchCenterName = (dispatchCenterId?: string | null) => {
    if (!dispatchCenterId) return null;
    const dispatchCenter = dispatchCenters?.find(
      (center) => center.id === dispatchCenterId
    );
    return dispatchCenter?.name || dispatchCenterId;
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Assignments</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {getInitials(user.firstName, user.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {getDisplayName(user.firstName, user.lastName)}
                    </p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    {user.phone && (
                      <p className="text-sm text-gray-400">{user.phone}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {user.roles.length === 0 ? (
                      <span className="text-gray-400 text-sm">No roles</span>
                    ) : (
                      [...new Set(user.roles)].map((role) => (
                        <Badge
                          key={role}
                          className={`${roleColors[role] || "bg-gray-500"} text-white`}
                          variant="secondary"
                        >
                          {role.replace("_", " ")}
                        </Badge>
                      ))
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1 text-sm">
                    {user.operator && (
                      <div className="flex items-center gap-1">
                        <Truck className="h-3 w-3" />
                        <span>
                          Operator
                          {getDispatchCenterName(
                            user.operator.dispatchCenterId
                          ) ??
                            (user.operator.depotId
                              ? ` @ ${user.operator.depotId}`
                              : "")}
                        </span>
                      </div>
                    )}
                    {!user.operator && (
                      <span className="text-gray-400">No assignments</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : "-"}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditUser(user)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit User
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {!user.operator && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleAssignOperator({
                              id: user.id,
                              name: getDisplayName(
                                user.firstName,
                                user.lastName
                              ),
                              operator: user.operator,
                            })
                          }
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Assign as Operator
                        </DropdownMenuItem>
                      )}
                      {!user.roles.includes("ADMIN") && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleAssignAdmin({
                              id: user.id,
                              name: getDisplayName(
                                user.firstName,
                                user.lastName
                              ),
                              operator: user.operator,
                            })
                          }
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          Assign as Admin
                        </DropdownMenuItem>
                      )}
                      {(user.operator ?? user.roles.includes("ADMIN")) && (
                        <DropdownMenuSeparator />
                      )}
                      {user.operator && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleUnassignOperator({
                              id: user.id,
                              name: getDisplayName(
                                user.firstName,
                                user.lastName
                              ),
                              operator: user.operator,
                            })
                          }
                          className="text-red-600 focus:text-red-600"
                        >
                          Remove Operator
                        </DropdownMenuItem>
                      )}
                      {user.roles.includes("ADMIN") && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleUnassignAdmin({
                              id: user.id,
                              name: getDisplayName(
                                user.firstName,
                                user.lastName
                              ),
                              operator: user.operator,
                            })
                          }
                          className="text-red-600 focus:text-red-600"
                        >
                          Remove Admin
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information for {editUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editFirstName">First Name</Label>
                <Input
                  id="editFirstName"
                  value={editUser?.firstName || ""}
                  onChange={(e) =>
                    setEditUser((prev) =>
                      prev ? { ...prev, firstName: e.target.value } : null
                    )
                  }
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editLastName">Last Name</Label>
                <Input
                  id="editLastName"
                  value={editUser?.lastName || ""}
                  onChange={(e) =>
                    setEditUser((prev) =>
                      prev ? { ...prev, lastName: e.target.value } : null
                    )
                  }
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editPhone">Phone</Label>
              <Input
                id="editPhone"
                type="tel"
                value={editUser?.phone || ""}
                onChange={(e) =>
                  setEditUser((prev) =>
                    prev ? { ...prev, phone: e.target.value } : null
                  )
                }
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmEditUser} disabled={updateUser.isPending}>
              {updateUser.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Operator Dialog */}
      <Dialog
        open={assignOperatorDialogOpen}
        onOpenChange={setAssignOperatorDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign as Operator</DialogTitle>
            <DialogDescription>
              Select a dispatch center to assign {selectedUser?.name} as an
              operator.
            </DialogDescription>
          </DialogHeader>
          <Select
            value={selectedDispatchCenterId}
            onValueChange={setSelectedDispatchCenterId}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select dispatch center" />
            </SelectTrigger>
            <SelectContent>
              {dispatchCenters?.map((center) => (
                <SelectItem key={center.id} value={center.id}>
                  {center.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAssignOperatorDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAssignOperator}
              disabled={!selectedDispatchCenterId || assignOperator.isPending}
            >
              {assignOperator.isPending ? "Assigning..." : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Admin Dialog */}
      <AlertDialog
        open={assignAdminDialogOpen}
        onOpenChange={setAssignAdminDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign Admin Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to grant admin privileges to{" "}
              {selectedUser?.name}? They will have full access to the admin
              dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAssignAdmin}
              disabled={assignAdmin.isPending}
            >
              {assignAdmin.isPending ? "Assigning..." : "Assign Admin"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unassign Confirmation Dialog */}
      <AlertDialog
        open={unassignDialogOpen}
        onOpenChange={setUnassignDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {unassignType === "operator"
                ? `This will remove the operator assignment from ${selectedUser?.name}. They will no longer be able to access the operator app.`
                : `This will remove admin privileges from ${selectedUser?.name}. They will no longer have admin access.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUnassign}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
